// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React from 'react';

import { useSlug } from '../hooks/useSlug';
import { Image, ImageProps } from '../Image/index';
import type { GlobalProps } from '../types';
import { classes } from '../utils';
import { RelativeDate, RelativeDateFormat, TeaseType } from '../';

import styles from './StoryCardSmall.module.css';

interface ImageTease {
  type: TeaseType.Image;
  value: Pick<
    ImageProps,
    | 'altText'
    | 'aspectRatio'
    | 'height'
    | 'origWidth'
    | 'origHeight'
    | 'src'
    | 'width'
  >;
}

type Tease = ImageTease;

export type StoryCardSmallProps = GlobalProps & {
  headline: string;
  // TODO - update locale to more specific type after https://jira.prod.bloomberg.com/browse/MEDIAWEBP-140 is done
  locale?: string;
  tease?: Tease;
  publishedAt?: string;
  updatedAt?: string;
  url: string;
  onClick?: () => void;
};

export const StoryCardSmall = React.forwardRef<
  HTMLAnchorElement,
  StoryCardSmallProps
>(function StoryCardSmallComponent(
  {
    className,
    headline,
    locale = 'en-US',
    name = 'StoryCardSmall',
    publishedAt,
    onClick,
    tease,
    updatedAt,
    unstyled,
    url,
  }: StoryCardSmallProps,
  ref
) {
  const componentName = useSlug(name);

  const cx = classes([styles.storyCardSmall, className || ''], {
    name,
    unstyled,
  });

  return (
    <a
      className={cx}
      data-component={componentName}
      data-testid={componentName}
      href={url}
      onClick={onClick}
      ref={ref}
    >
      <article>
        {tease?.type === TeaseType.Image ? (
          <Image
            altText={tease.value.altText}
            aspectRatio={tease.value.aspectRatio}
            className="image"
            height={tease.value.height}
            isLazy
            origHeight={tease.value.origHeight}
            origWidth={tease.value.origWidth}
            src={tease.value.src}
            width={tease.value.width}
          />
        ) : null}
        <div>
          <p className="headline" data-testid={`${componentName}--headline`}>
            {headline}
          </p>
          <RelativeDate
            locale={locale}
            format={RelativeDateFormat.LONG}
            publishedAt={publishedAt && new Date(publishedAt).toISOString()}
            updatedAt={updatedAt && new Date(updatedAt).toISOString()}
          />
        </div>
      </article>
    </a>
  );
});
